import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { NotebookPen, Trash2, Send, Shield } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

const TEAL = "#0D9488";
const AMBER = "#F59E0B";

/** Coach-private + admin-support notes about a specific client.
 *  - Coaches see: their own notes + any admin-support notes on this client.
 *  - Admins see: every note on this client (their own + every coach's).
 *  Backed by db.coach_notes with `is_admin_note` flag.
 */
export default function CoachNotesPanel({ clientId, clientHandle, admin = false, readOnly = false }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/coach/clients/${clientId}/notes`)
      .then(({ data }) => setNotes(Array.isArray(data) ? data : []))
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!clientId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/coach/clients/${clientId}/notes`, { body: draft.trim() });
      setNotes((prev) => [data, ...prev]);
      setDraft("");
      toast.success("Note saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (note) => {
    try {
      await api.delete(`/coach/clients/${clientId}/notes/${note.id}`);
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
      toast.success("Note deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="coach-notes-panel">
      <div className="flex items-center gap-2 mb-3">
        <NotebookPen className="w-4 h-4" style={{ color: TEAL }} />
        <h3 className="font-display font-bold text-slate-900">Private notes</h3>
        <span className="text-[10px] text-slate-400 ml-1 tabular-nums">{notes.length}</span>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-slate-400 font-mono-num">
          {readOnly ? "Admin support view · read-only" : admin ? "Admin support · note visible to the coach" : "Coach-private · admin can also see"}
        </span>
      </div>

      {!readOnly && (
        <form onSubmit={submit} className="mb-4">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, 2000))}
            placeholder={admin
              ? `Admin note about ${clientHandle ? "@" + clientHandle : "this client"} — the coach will see this…`
              : `Follow-up notes about ${clientHandle ? "@" + clientHandle : "this client"}…`}
            rows={2}
            className={`bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] text-sm ${admin ? "border-amber-200 focus-visible:border-amber-400" : ""}`}
            data-testid="coach-note-input"
          />
          <div className="flex items-center justify-between mt-2">
            <div className="text-[10px] text-slate-400 font-mono-num">{draft.length}/2000</div>
            <Button
              type="submit"
              size="sm"
              disabled={!draft.trim() || saving}
              className="text-white"
              style={{ background: admin ? AMBER : TEAL }}
              data-testid="coach-note-save"
            >
              {admin ? <Shield className="w-3.5 h-3.5 mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              {saving ? "Saving…" : admin ? "Add admin note" : "Add note"}
            </Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-xs text-slate-500 italic">Loading notes…</div>
      ) : notes.length === 0 ? (
        <div className="text-xs text-slate-500 italic">No notes yet — jot your first follow-up above.</div>
      ) : (
        <ul className="space-y-2 max-h-64 overflow-auto" data-testid="coach-notes-list">
          {notes.map((n) => (
            <li
              key={n.id}
              className={`border rounded-md p-3 group relative ${n.is_admin_note ? "border-amber-200 bg-amber-50/40" : "border-slate-100 bg-slate-50/40"}`}
              data-testid={`coach-note-row-${n.id}`}
            >
              {n.is_admin_note && (
                <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-amber-100 text-amber-700 mb-1.5"
                     data-testid={`coach-note-admin-badge-${n.id}`}>
                  <Shield className="w-2.5 h-2.5" /> Admin{n.author_name ? ` · ${n.author_name}` : ""}
                </div>
              )}
              <div className="text-sm text-slate-800 whitespace-pre-wrap pr-6">{n.body}</div>
              <div className="text-[10px] text-slate-400 font-mono-num mt-1.5">
                {(n.created_at || "").slice(0, 16).replace("T", " · ")}
              </div>
              {!readOnly && (admin || !n.is_admin_note) && (
                <button
                  type="button"
                  onClick={() => remove(n)}
                  className="absolute top-2 right-2 opacity-60 hover:opacity-100 focus:opacity-100 transition-opacity w-7 h-7 rounded flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                  data-testid={`coach-note-delete-${n.id}`}
                  aria-label="Delete note"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
